import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BudgetProposalT,
    BudgetProposalWithResourcesT,
    BudgetT,
} from 'routes/BudgetModule/config/types';
import { Typography } from '@mui/material';
import { BUDGET_NAME_FIELD } from 'routes/BudgetModule/config/constants';
import {
    BudgetBys,
    MANTY_ORDERED_MOVABLE_BUDGET_PHASES,
} from 'business/constants';
import {
    AutocompletAble,
    formatValueForAutocomplete,
    ModalFormItem,
} from 'ui/DropdownItems/types';
import GenericAutocomplete from 'ui/DropdownItems/GenericAutocomplete';
import ResetWrapper from 'ui/DropdownItems/ResetWrapper';
import { isCustomBudgetProposal } from 'routes/BudgetModule/components/BudgetProposal/Resource/hooks/budgetProposal';
import CreateBPSummary from './AddBudgetSummary';
import { useCreateBP } from './hooks';
import { triggerTrackerConfirmNewBudgetCreation } from '../trackersHelpers';
import { StyMultiStepModal } from '../../shared/StyMultiStepModal';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';

type ModalProps = {
    isModalShown: boolean;
    budgetProposals: BudgetProposalWithResourcesT[];
    setShowModal: DispatchState<boolean>;
    budgets: BudgetT[];
};

const getAvailableVentilation = (
    bps: BudgetProposalT[],
    year: number,
    t: TranslationT,
): AutocompletAble<string>[] => {
    const ventilations: AutocompletAble<string>[] = [
        formatValueForAutocomplete(
            BudgetBys.ORG_CHART_FINANCE,
            false,
            t('adminBudgetSpace.by.service'),
        ),
        formatValueForAutocomplete(
            BudgetBys.GESTIONNAIRE,
            false,
            t('adminBudgetSpace.by.gestionnaire'),
        ),
    ];
    bps.filter((e) => e.exercice === year).forEach((e) => {
        const targetOption = ventilations.find((u) => u.value !== e.by);
        if (targetOption) targetOption.disabled = true;
    });
    return ventilations;
};

const isDisabledBudget = (
    budget: BudgetT,
    bps: BudgetProposalWithResourcesT[],
    year: number,
) => {
    const candidateBp = bps?.filter((e) => e.exercice === year);
    if (!candidateBp || candidateBp.length < 1) return false;
    return (
        candidateBp.find((e) => e.budgetName === budget[BUDGET_NAME_FIELD]) !==
        undefined
    );
};

const getNextYear = () => new Date().getFullYear() + 1;

export default function CreateBPModal({
    isModalShown,
    setShowModal,
    budgetProposals: bps,
    budgets,
}: ModalProps): React.ReactElement {
    const { t } = useTranslation();

    const [availableBys, setAvailableBys] = useState<AutocompletAble<string>[]>(
        [],
    );
    const [availableYears, setAvailableYears] = useState<
        AutocompletAble<number>[]
    >([]);
    const [availableBudgets, setAvailableBudgets] = useState<
        AutocompletAble<BudgetT>[]
    >([]);
    const availablePhases: AutocompletAble<string>[] =
        MANTY_ORDERED_MOVABLE_BUDGET_PHASES.map((e) =>
            formatValueForAutocomplete(e, false, `${t(`budgetProposal.${e}`)}`),
        );

    const [year, setYear] = useState<AutocompletAble<number> | null>(null);
    const [budgetCodesNames, setBudgetCodesNames] = useState<
        AutocompletAble<BudgetT>[] | null
    >(null);
    const [by, setBy] = useState<AutocompletAble<string> | null>(null);
    const [phase, setPhase] = useState<AutocompletAble<string> | null>(null);

    useEffect(() => {
        if (bps === undefined) return;
        const nextYear = getNextYear();

        const years: AutocompletAble<number>[] = Array.from(
            { length: 6 },
            (_, i) =>
                formatValueForAutocomplete(
                    5 - i + nextYear - 5,
                    false,
                    `${5 - i + nextYear - 5}`,
                ),
        );
        setAvailableYears(years);
    }, [bps]);

    useEffect(() => {
        if (budgets === undefined || year === null) return;
        const candidateBudgets: AutocompletAble<BudgetT>[] = [];
        budgets.forEach((e) => {
            candidateBudgets.push(
                formatValueForAutocomplete(
                    e,
                    isDisabledBudget(e, bps, year.value),
                    `${e[BUDGET_NAME_FIELD]} ${year.value}`,
                ),
            );
        });
        setAvailableBudgets(candidateBudgets);
    }, [year, budgets, bps]);

    useEffect(() => {
        if (bps === undefined || year === null) return;
        setAvailableBys(getAvailableVentilation(bps, year.value, t));
    }, [bps, year, t]);

    const autocompletes: Array<ModalFormItem<string | number | BudgetT>> = [
        {
            children: (
                <GenericAutocomplete
                    selectedValues={year}
                    availableValues={availableYears}
                    setSelectedValues={setYear}
                    label={t('adminBudgetSpace.addBudgetModal.year')}
                />
            ),
            value: year,
            setValue: setYear,
        },
        {
            children: (
                <GenericAutocomplete
                    multiple
                    selectedValues={budgetCodesNames}
                    availableValues={availableBudgets}
                    setSelectedValues={setBudgetCodesNames}
                    label={t('adminBudgetSpace.addBudgetModal.budgetCode')}
                />
            ),
            value: budgetCodesNames,
            setValue: setBudgetCodesNames,
        },
        {
            children: (
                <GenericAutocomplete
                    selectedValues={by}
                    availableValues={availableBys}
                    setSelectedValues={setBy}
                    label={t('adminBudgetSpace.addBudgetModal.ventilation')}
                />
            ),
            value: by,
            setValue: setBy,
        },
        {
            children: (
                <GenericAutocomplete
                    selectedValues={phase}
                    availableValues={availablePhases}
                    setSelectedValues={setPhase}
                    label={t('adminBudgetSpace.addBudgetModal.phase')}
                />
            ),
            value: phase,
            setValue: setPhase,
        },
    ];

    // SCHOOL --
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);

    const {
        mutate: createBP,
        isPending,
        data: summary,
    } = useCreateBP(
        isSchool,
        year?.value,
        by?.value,
        phase?.value,
        budgetCodesNames?.map((x) => x.value),
    );

    const onConfirmAction = () => {
        createBP();
        triggerTrackerConfirmNewBudgetCreation();
    };

    const steps = [
        {
            confirmAction: {
                enabled: phase !== null,
                onClick: onConfirmAction,
                label: 'common.confirm',
            },
            stepBackAction: {
                onClick: () => {
                    setShowModal(false);
                },
                enabled: true,
            },
            node: (
                <>
                    <Typography variant="h5" sx={{ paddingBottom: '1.5em' }}>
                        {t('adminBudgetSpace.addBudgetModal.title')}
                    </Typography>
                    <ResetWrapper items={autocompletes} />
                </>
            ),
        },
        {
            confirmAction: {
                enabled: !isPending,
                label: 'common.close',
            },
            stepBackAction: {
                enabled: false,
            },
            node: <CreateBPSummary summary={summary} isLoading={isPending} />,
            disableClose: isPending,
        },
    ];

    return (
        <StyMultiStepModal
            isOpen={isModalShown}
            onClose={() => {
                setShowModal(false);
            }}
            maxWidth="md"
            steps={steps}
        />
    );
}
